import React, {useEffect, useMemo, useState} from "react";
import {ProgressBarBasic} from "../../../../../../components/progressBar/ProgressBar";
import {checkDiligenceAsset} from "../../../../../../api/asset/DiligenceService";
import {handleActiveError} from "../../../../../../components/sonner/ToastBasic";
import SearchDummyText from "../../../../../../components/input/SearchDummyText";
import DiligenceAssetMessageModal from "./DiligenceAssetMessageModal";
import {DetailPanel} from "../../../../../../components/detailLayout";
import {useSelector} from "react-redux";
import "./diligenceAssets.css";

const SEARCH_OPTIONS = [
    {id: 0, name: "자산명", value: "assetName", placeHolder: "자산명 검색"},
    {id: 1, name: "자산사용자", value: "handler", placeHolder: "자산사용자 명"},
];

function DiligenceAssets({data}) {
    const [assets, setAssets] = useState(data.assets ?? []);
    const [assetsSearch, setAssetsSearch] = useState(data.assets ?? []);
    const [updateData, setUpdateData] = useState((data.assets ?? []).map((e) => e.message));
    const [rawData, setRawData] = useState([]);
    const [messageModal, setMessageModal] = useState(null);
    const user = useSelector((state) => state.auth.user);

    useEffect(() => {
        const nextAssets = data.assets ?? [];
        setAssets(nextAssets);
        setAssetsSearch(nextAssets);
        setUpdateData(nextAssets.map((e) => e.message));
    }, [data.assets]);

    const doneCount = useMemo(
        () => (data.assets ?? []).filter((e) => e.checked === true).length,
        [data.assets],
    );
    const totalCount = data.assets?.length ?? 0;
    const progressPercent = totalCount === 0 ? 0 : ((doneCount / totalCount) * 100).toFixed(1);

    const getFormattedDate = () => {
        const now = new Date();
        const yyyy = now.getFullYear();
        const mm = String(now.getMonth() + 1).padStart(2, "0");
        const dd = String(now.getDate()).padStart(2, "0");
        const hh = String(now.getHours()).padStart(2, "0");
        const ii = String(now.getMinutes()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd} ${hh}:${ii}`;
    };

    const findAssetIndex = (assetId) => assets.findIndex((item) => item.asset?.id === assetId);

    const doDiligenceAsset = (diligenceId, assetId, message, index) => {
        if (!window.confirm("완료하면 수정할 수 없습니다.")) {
            return;
        }

        checkDiligenceAsset(diligenceId, assetId, message).then((response) => {
            if (response.status === 200) {
                alert("저장되었습니다.");
                const newData = [...rawData];
                newData[index] = message;
                setRawData(newData);

                const newAsset = [...assets];
                newAsset[index].checked = true;
                setAssets(newAsset);

                setAssetsSearch((prev) => {
                    const updated = prev.map((item) =>
                        item.asset?.id === assetId
                            ? {...item, checked: true, checkedAt: getFormattedDate(), operator: user}
                            : item,
                    );
                    return updated;
                });
            } else if (response.status === 201 || response.status === 202) {
                handleActiveError({message: response.data.title, description: response.data.reason});
            }
        });
    };

    return (
        <div className="diligence-assets-page">
            <DetailPanel title="진행정보" className="diligence-assets-progress-panel">
                <div className="diligence-assets-progress-card">
                    <div className="diligence-assets-progress-layout">
                        <section className="diligence-assets-progress-summary" aria-label="실사 진행률">
                            <div
                                className="diligence-assets-progress-ring"
                                style={{"--progress": progressPercent}}
                                aria-hidden="true"
                            >
                                <span className="diligence-assets-progress-ring__label">{progressPercent}%</span>
                            </div>
                            <div className="diligence-assets-progress-summary__body">
                                <span className="diligence-assets-progress-title">실사 진행률</span>
                                <div className="diligence-assets-progress-stats">
                                    <span className="diligence-assets-progress-stat is-done">
                                        <strong>{doneCount}</strong> 완료
                                    </span>
                                    <span className="diligence-assets-progress-stat is-pending">
                                        <strong>{totalCount - doneCount}</strong> 남음
                                    </span>
                                    <span className="diligence-assets-progress-stat is-total">
                                        전체 <strong>{totalCount}</strong>
                                    </span>
                                </div>
                                <div className="diligence-assets-progress-bar-wrap">
                                    <ProgressBarBasic done={doneCount} total={totalCount} />
                                </div>
                            </div>
                        </section>

                        <section className="diligence-assets-search-section" aria-label="자산 검색">
                            <span className="diligence-assets-search-section__label">자산 검색</span>
                            <SearchDummyText
                                assets={assets}
                                setAssetsSearch={setAssetsSearch}
                                options={SEARCH_OPTIONS}
                            />
                        </section>
                    </div>
                </div>
            </DetailPanel>

            <DetailPanel title="자산 정보" className="diligence-assets-list-panel">
                <div className="diligence-assets-list-meta">
                    총 {assetsSearch.length}건
                    {assetsSearch.length !== totalCount ? ` (전체 ${totalCount}건 중)` : ""}
                </div>
                <div className="diligence-assets-table-wrap">
                    {assetsSearch.length === 0 ? (
                        <div className="diligence-assets-empty">검색 결과가 없습니다.</div>
                    ) : (
                        <>
                            <div className="diligence-assets-grid-head">
                                <span>자산명</span>
                                <span>자산코드</span>
                                <span>자산사용자</span>
                                <span className="mobile-hidden">자산그룹</span>
                                <span className="mobile-hidden">업무그룹</span>
                                <span className="mobile-hidden">처리일 · 처리자</span>
                                <span>처리 · 실사내용</span>
                            </div>
                            {assetsSearch.map((each) => {
                                const rowIndex = findAssetIndex(each.asset?.id);
                                const message = updateData[rowIndex];
                                const hasMessage = Boolean(message);

                                return (
                                    <div key={each.asset?.id} className="diligence-assets-grid-row">
                                        <div className="diligence-assets-grid-cell diligence-assets-grid-cell--name">
                                            <span
                                                className="diligence-assets-grid-cell__title"
                                                title={each.asset?.assetName}
                                            >
                                                {each.asset?.assetName}
                                            </span>
                                            <span className="diligence-assets-grid-cell__meta">
                                                {[each.asset?.assetCode, each.asset?.handler?.name]
                                                    .filter(Boolean)
                                                    .join(" · ")}
                                            </span>
                                        </div>
                                        <span
                                            className="diligence-assets-grid-cell mobile-hidden"
                                            title={each.asset?.assetCode}
                                        >
                                            {each.asset?.assetCode}
                                        </span>
                                        <span
                                            className="diligence-assets-grid-cell mobile-hidden"
                                            title={each.asset?.handler?.name}
                                        >
                                            {each.asset?.handler?.name}
                                        </span>
                                        <span
                                            className="diligence-assets-grid-cell mobile-hidden"
                                            title={each.asset?.group?.name}
                                        >
                                            {each.asset?.group?.name}
                                        </span>
                                        <span
                                            className="diligence-assets-grid-cell mobile-hidden"
                                            title={each.asset?.task?.name}
                                        >
                                            {each.asset?.task?.name}
                                        </span>
                                        <span className="diligence-assets-grid-cell mobile-hidden">
                                            {each.checkedAt?.substring(0, 16)}
                                            {each.checked && each.operator?.name
                                                ? ` [${each.operator.name}]`
                                                : ""}
                                        </span>
                                        <div className="diligence-assets-grid-cell diligence-assets-grid-cell--actions">
                                            {each.checked ? (
                                                <button
                                                    type="button"
                                                    className="diligence-assets-action-btn diligence-assets-action-btn--done"
                                                    disabled
                                                >
                                                    완료됨
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="diligence-assets-action-btn diligence-assets-action-btn--primary"
                                                    onClick={() =>
                                                        doDiligenceAsset(
                                                            data.id,
                                                            each.asset.id,
                                                            message,
                                                            rowIndex,
                                                        )
                                                    }
                                                >
                                                    실사처리
                                                </button>
                                            )}

                                            <button
                                                type="button"
                                                className={`diligence-assets-action-btn ${
                                                    each.checked
                                                        ? ""
                                                        : hasMessage
                                                          ? "diligence-assets-action-btn--filled"
                                                          : ""
                                                }`}
                                                onClick={() =>
                                                    setMessageModal({
                                                        asset: each.asset,
                                                        rowIndex,
                                                        readOnly: each.checked,
                                                    })
                                                }
                                            >
                                                {each.checked
                                                    ? "조회"
                                                    : hasMessage
                                                      ? "조회 · 수정"
                                                      : "실사내용 입력"}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </>
                    )}
                </div>
            </DetailPanel>

            {messageModal ? (
                <DiligenceAssetMessageModal
                    open
                    onClose={() => setMessageModal(null)}
                    diligenceId={data.id}
                    asset={messageModal.asset}
                    rowIndex={messageModal.rowIndex}
                    message={updateData[messageModal.rowIndex]}
                    setUpdateData={setUpdateData}
                    readOnly={messageModal.readOnly}
                />
            ) : null}
        </div>
    );
}

export default DiligenceAssets;
